import { useTranslation } from 'react-i18next';
import { ReportsRopaArea } from '../Area';
import { FILTER } from '../filter-types';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BG = 'var(--vo-mist)';

const SCOPE_BADGE = {
  'Empresa':           { color: 'var(--vo-cobalto)', bg: 'var(--vo-cobalto-8)' },
  'Área':              { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  'Processo':          { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  'Sumário Executivo': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
};

const HeaderCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const CompanyLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 4px;
`;
const ReportName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 8px;
`;
const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;
const ScopeBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 3px 12px;
  border-radius: 20px;
  white-space: nowrap;
`;
const MetaText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;
const EmptyCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 48px 24px;
  text-align: center;
  color: ${MUTED};
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const formatDate = d => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString('pt-BR');
};

export const ReportsRopaCompany = ({
  company,
  ropaName,
  ropaScope,
  ropaDate,
  filter,
  area,
  process,
  data,
  category,
  legalBase,
  processing,
  position,
}) => {
  const { t } = useTranslation(['dashboard', 'mapping']);

  const filteredAreas = company.areas?.filter(areaFilter => {
    if (filter === FILTER.AREA && !!area)
      return areaFilter.name === area;
    if (filter === FILTER.PROCESS && !!process)
      return areaFilter.processes.some(p => p.name === process);
    if (filter === FILTER.DATA && !!data)
      return areaFilter.processes.some(p => p.dataMapping.some(dm => dm.name === data));
    if (filter === FILTER.CATEGORY && !!category)
      return areaFilter.processes.some(p => p.dataMapping.some(dm => dm.category === category));
    if (filter === FILTER.PROCESSING && !!processing)
      return areaFilter.processes.some(p =>
        p.dataMapping.some(dm => dm.dataProcessing.some(dp => dp.processing === processing)));
    if (filter === FILTER.LEGAL_BASE && !!legalBase)
      return areaFilter.processes.some(p =>
        p.dataMapping.some(dm => dm.dataProcessing.some(dp => dp.legalBase === legalBase)));
    if (filter === FILTER.POSITION && !!position)
      return areaFilter.processes.some(p =>
        p.dataMapping.some(dm => dm.dataProcessing.some(dp => dp.position === position)));
    return true;
  });

  const processCount = company.areas?.reduce((s, a) => s + (a.processes?.length || 0), 0) ?? 0;
  const dataCount = company.areas?.reduce((s, a) =>
    s + (a.processes?.reduce((ps, p) => ps + (p.dataMapping?.length || 0), 0) ?? 0), 0) ?? 0;
  const areaCount = company.areas?.length ?? 0;

  const badge = SCOPE_BADGE[ropaScope] ?? { color: MUTED, bg: BG };

  return (
    <>
      <HeaderCard>
        <CompanyLabel>{company.name}</CompanyLabel>
        <ReportName>{ropaName}</ReportName>
        <MetaRow>
          <ScopeBadge $color={badge.color} $bg={badge.bg}>
            {t('dashboard:ropa.show.scopeLabel')}: {ropaScope}
          </ScopeBadge>
          <MetaText>{t('dashboard:ropa.table.generatedAt')} {formatDate(ropaDate)}</MetaText>
          <MetaText>
            {areaCount} {t('dashboard:ropa.show.statsAreas')} · {processCount} {t('dashboard:ropa.show.statsProcesses')} · {dataCount} {t('dashboard:ropa.show.statsData')}
          </MetaText>
        </MetaRow>
      </HeaderCard>

      {(!filteredAreas || filteredAreas.length === 0) ? (
        <EmptyCard>{t('mapping:ropa.company.noAreas')}</EmptyCard>
      ) : (
        filteredAreas.map((itemArea, index) => (
          <ReportsRopaArea
            key={itemArea.name ?? index}
            area={itemArea}
            filter={filter}
            process={process}
            data={data}
            category={category}
            legalBase={legalBase}
            processing={processing}
            position={position}
          />
        ))
      )}
    </>
  );
};
